import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

interface QuestionOption {
  id: string;
  text: string;
}

interface Question {
  id: number;
  question: string;
  options: QuestionOption[];
}

interface QuizSectionProps {
  currentQuestion: Question;
  currentQuestionIndex: number;
  totalQuestions: number;
  onAnswerSelect: (optionId: string, optionText: string) => void;
  onPrevQuestion: () => void;
  canGoPrev: boolean;
}

export function QuizSection({
  currentQuestion,
  currentQuestionIndex,
  totalQuestions,
  onAnswerSelect,
  onPrevQuestion,
  canGoPrev,
}: QuizSectionProps) {
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  return (
    <motion.div
      key="quiz"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">
            问题 {currentQuestionIndex + 1} / {totalQuestions}
          </h2>
          <Button variant="outline" size="sm" onClick={onPrevQuestion} disabled={!canGoPrev}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            上一题
          </Button>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{currentQuestion.question}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {currentQuestion.options.map((option) => (
            <Button
              key={option.id}
              variant="outline"
              className="w-full text-left justify-start h-auto p-4 hover:bg-foreground/10"
              onClick={() => onAnswerSelect(option.id, option.text)}
            >
              <div className="flex items-start space-x-3">
                <span className="font-bold text-red-500">{option.id}.</span>
                <span className="flex-1">{option.text}</span>
              </div>
            </Button>
          ))}
        </CardContent>
      </Card>
    </motion.div>
  );
}
